import type { ShapePoint, ShapeVertex } from '@repo/api-contracts';
import { isSmoothVertex } from './toggle-smooth';

export type VertexHandle = 'handleIn' | 'handleOut';

const withHandle = (vertex: ShapeVertex, which: VertexHandle, to: ShapePoint): ShapeVertex => {
  const handle = { xMm: to.xMm, yMm: to.yMm };
  return which === 'handleIn' ? { ...vertex, handleIn: handle } : { ...vertex, handleOut: handle };
};

const shifted = (point: ShapePoint, dx: number, dy: number): ShapePoint => ({
  xMm: point.xMm + dx,
  yMm: point.yMm + dy,
});

/** 점 하나를 to 로 옮긴다 — 손잡이도 같은 만큼 따라 옮긴다. */
export const moveVertexTo = (
  points: readonly ShapeVertex[],
  index: number,
  to: ShapePoint,
): ShapeVertex[] =>
  points.map((point, position) => {
    if (position !== index) return point;
    const dx = to.xMm - point.xMm;
    const dy = to.yMm - point.yMm;
    return {
      xMm: to.xMm,
      yMm: to.yMm,
      ...(point.handleIn === undefined ? {} : { handleIn: shifted(point.handleIn, dx, dy) }),
      ...(point.handleOut === undefined ? {} : { handleOut: shifted(point.handleOut, dx, dy) }),
    };
  });

/**
 * 점의 손잡이 하나를 to 로 옮긴다. 부드러운 점(isSmoothVertex)은 반대 손잡이가 같은 직선 반대쪽으로 따라오고 자기 길이는
 * 그대로다. independent(Alt)면 반대 손잡이를 두어 꺾는다. 손잡이를 점 위에 놓으면 반대는 그대로 둔다.
 */
export const moveHandleTo = (
  points: readonly ShapeVertex[],
  index: number,
  which: VertexHandle,
  to: ShapePoint,
  { independent }: { independent: boolean },
): ShapeVertex[] =>
  points.map((point, position) => {
    if (position !== index) return point;
    const moved = withHandle(point, which, to);
    const otherKey: VertexHandle = which === 'handleIn' ? 'handleOut' : 'handleIn';
    const other = point[otherKey];
    if (independent || other === undefined || !isSmoothVertex(point)) return moved;
    const dx = to.xMm - point.xMm;
    const dy = to.yMm - point.yMm;
    const length = Math.hypot(dx, dy);
    if (length === 0) return moved;
    const otherLength = Math.hypot(other.xMm - point.xMm, other.yMm - point.yMm);
    return withHandle(moved, otherKey, {
      xMm: point.xMm - (dx / length) * otherLength,
      yMm: point.yMm - (dy / length) * otherLength,
    });
  });

/** 모든 점과 손잡이를 dx·dy 만큼 옮긴다(컴포넌트 mm ↔ 표면 mm). */
export const translateVertices = (
  points: readonly ShapeVertex[],
  dx: number,
  dy: number,
): ShapeVertex[] =>
  points.map((point) => ({
    ...shifted(point, dx, dy),
    ...(point.handleIn === undefined ? {} : { handleIn: shifted(point.handleIn, dx, dy) }),
    ...(point.handleOut === undefined ? {} : { handleOut: shifted(point.handleOut, dx, dy) }),
  }));
